import { useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowDown, ArrowLeft, ArrowRight, ArrowUp, Eye, EyeOff, ImageOff, Info, Loader2, Plus, Trash2, Upload, X } from 'lucide-react'
import { api, errorMessage } from '@/lib/api'
import { cn } from '@/lib/cn'
import type { AdminOptionGroup, AdminOptionValue } from '@/types'
import Card, { SectionLabel } from '@/components/ui/Card'
import PageHeader from '@/components/ui/PageHeader'
import Button from '@/components/ui/Button'
import ConfirmDialog from '@/components/ui/ConfirmDialog'
import Modal from '@/components/ui/Modal'
import { Field, Input } from '@/components/ui/Field'
import { Select } from '@/components/ui/Select'
import { Table, Row, Cell, EmptyRow, actionsColumn, actionsCellClass } from '@/components/ui/Table'
import { useToast } from '@/components/ui/toast'
import LogoLoader from '@/components/ui/LogoLoader'

type Display = AdminOptionGroup['display']

const displayOptions: { value: Display; label: string }[] = [
  { value: 'select', label: 'Dropdown' },
  { value: 'picture', label: 'Pictures' },
]

/** Every write answers with the whole list. */
type ListResponse = { message: string; data: AdminOptionGroup[] }

/** Anything that shows a group or a value, refreshed after every change here. */
const DEPENDENT_QUERIES = ['option-groups', 'options', 'admin-products', 'products', 'products-picker', 'storefront-products', 'storefront-product']

/**
 * Admin > Products > Product Options: the garment option groups (Collar type,
 * Sleeve type, Cutting, Material, and any the admin adds) and their values.
 * What is set up here is what product edit offers, and what the configurator,
 * checkout, the manual order form and every order page read. Laid out like
 * Settings > Checklist: the groups as chips, the chosen group's values as a
 * table.
 */
export default function AdminProductOptions() {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [groupId, setGroupId] = useState<number | null>(null)
  // null while the "Add group" form is closed.
  const [newGroup, setNewGroup] = useState<{ label: string; display: Display } | null>(null)
  const [labelDraft, setLabelDraft] = useState<string | null>(null)
  const [valueText, setValueText] = useState('')
  const [edits, setEdits] = useState<Record<number, string>>({})
  const [deletingValue, setDeletingValue] = useState<AdminOptionValue | null>(null)
  const [deletingGroup, setDeletingGroup] = useState<AdminOptionGroup | null>(null)
  // The value whose Skips are being edited, and the ticks so far.
  const [skipping, setSkipping] = useState<{ value: AdminOptionValue; keys: string[] } | null>(null)

  const list = useQuery({
    queryKey: ['admin-option-groups'],
    queryFn: async () => (await api.get<{ data: AdminOptionGroup[] }>('/admin/option-groups')).data.data,
  })

  const refresh = (res: ListResponse) => {
    queryClient.setQueryData(['admin-option-groups'], res.data)
    for (const key of DEPENDENT_QUERIES) {
      queryClient.invalidateQueries({ queryKey: [key] })
    }
    toast.success(res.message)
  }
  const fail = (title: string) => (err: unknown) => toast.error(title, errorMessage(err))

  const addGroup = useMutation({
    mutationFn: async (body: { label: string; display: Display }) =>
      (await api.post<ListResponse & { group: { id: number } }>('/admin/option-groups', body)).data,
    onSuccess: (res) => {
      refresh(res)
      setGroupId(res.group.id)
      setNewGroup(null)
      setLabelDraft(null)
      setEdits({})
    },
    onError: fail('Could not add the group'),
  })

  const updateGroup = useMutation({
    mutationFn: async ({ id, ...body }: { id: number; label?: string; display?: Display; active?: boolean }) =>
      (await api.patch<ListResponse>(`/admin/option-groups/${id}`, body)).data,
    onSuccess: (res, vars) => {
      refresh(res)
      if (vars.label !== undefined) setLabelDraft(null)
    },
    onError: fail('Save failed'),
  })

  const deleteGroup = useMutation({
    mutationFn: async (id: number) => (await api.delete<ListResponse>(`/admin/option-groups/${id}`)).data,
    onSuccess: (res) => {
      refresh(res)
      setDeletingGroup(null)
      setGroupId(null)
    },
    onError: (err) => {
      setDeletingGroup(null)
      fail('Could not delete the group')(err)
    },
  })

  const reorderGroups = useMutation({
    mutationFn: async (ids: number[]) => (await api.put<ListResponse>('/admin/option-groups/reorder', { ids })).data,
    onSuccess: refresh,
    onError: fail('Reorder failed'),
  })

  const addValue = useMutation({
    mutationFn: async ({ id, value }: { id: number; value: string }) =>
      (await api.post<ListResponse>(`/admin/option-groups/${id}/values`, { value })).data,
    onSuccess: (res) => {
      refresh(res)
      setValueText('')
    },
    onError: fail('Could not add the value'),
  })

  const renameValue = useMutation({
    mutationFn: async ({ id, value }: { id: number; value: string }) =>
      (await api.patch<ListResponse>(`/admin/option-values/${id}`, { value })).data,
    onSuccess: (res, vars) => {
      refresh(res)
      setEdits((prev) => {
        const next = { ...prev }
        delete next[vars.id]
        return next
      })
    },
    onError: fail('Rename failed'),
  })

  const saveSkips = useMutation({
    mutationFn: async ({ id, skips }: { id: number; skips: string[] }) =>
      (await api.patch<ListResponse>(`/admin/option-values/${id}`, { skips })).data,
    onSuccess: (res) => {
      refresh(res)
      setSkipping(null)
    },
    onError: fail('Could not save'),
  })

  const deleteValue = useMutation({
    mutationFn: async (id: number) => (await api.delete<ListResponse>(`/admin/option-values/${id}`)).data,
    onSuccess: (res) => {
      refresh(res)
      setDeletingValue(null)
    },
    onError: (err) => {
      setDeletingValue(null)
      fail('Could not delete the value')(err)
    },
  })

  const reorderValues = useMutation({
    mutationFn: async ({ id, ids }: { id: number; ids: number[] }) =>
      (await api.put<ListResponse>(`/admin/option-groups/${id}/values/reorder`, { ids })).data,
    onSuccess: refresh,
    onError: fail('Reorder failed'),
  })

  const uploadImage = useMutation({
    mutationFn: async ({ id, file }: { id: number; file: File }) => {
      const form = new FormData()
      form.append('image', file)
      return (await api.post<ListResponse>(`/admin/option-values/${id}/image`, form)).data
    },
    onSuccess: refresh,
    onError: fail('Upload failed'),
  })

  const removeImage = useMutation({
    mutationFn: async (id: number) => (await api.delete<ListResponse>(`/admin/option-values/${id}/image`)).data,
    onSuccess: refresh,
    onError: fail('Could not remove the picture'),
  })

  if (list.isLoading) return <LogoLoader />

  const groups = list.data ?? []
  const selected = groups.find((group) => group.id === groupId) ?? groups[0]
  const values = selected?.values ?? []
  const groupIndex = selected ? groups.indexOf(selected) : -1
  const pictures = selected?.display === 'picture'
  const labelValue = labelDraft ?? selected?.label ?? ''
  const labelDirty = Boolean(selected) && labelDraft !== null && labelDraft.trim() !== '' && labelDraft.trim() !== selected?.label

  const selectGroup = (id: number) => {
    setGroupId(id)
    setLabelDraft(null)
    setValueText('')
    setEdits({})
  }

  // "Sleeve type", or "None", from the group keys a value skips.
  const skipLabel = (keys: string[] | null | undefined) =>
    groups.filter((group) => keys?.includes(group.key)).map((group) => group.label).join(', ') || 'None'

  const moveGroup = (direction: -1 | 1) => {
    const ids = groups.map((group) => group.id)
    const target = groupIndex + direction
    if (groupIndex < 0 || target < 0 || target >= ids.length) return
    ;[ids[groupIndex], ids[target]] = [ids[target]!, ids[groupIndex]!]
    reorderGroups.mutate(ids)
  }

  const moveValue = (index: number, direction: -1 | 1) => {
    if (!selected) return
    const ids = values.map((value) => value.id)
    const target = index + direction
    if (target < 0 || target >= ids.length) return
    ;[ids[index], ids[target]] = [ids[target]!, ids[index]!]
    reorderValues.mutate({ id: selected.id, ids })
  }

  return (
    <div>
      <PageHeader title="Product Options" subtitle="The garment choices products offer: collar, sleeve, cutting, fabric and any group you add" />

      <Card className="mb-5">
        <SectionLabel>Groups</SectionLabel>
        <div className="flex flex-wrap gap-1.5">
          {groups.map((group) => {
            const on = group.id === selected?.id
            return (
              <button
                key={group.id}
                type="button"
                onClick={() => selectGroup(group.id)}
                title={group.active ? undefined : 'Hidden from products and new orders'}
                className={cn(
                  'rounded-lg border px-3 py-1.5 text-sm font-semibold',
                  on ? 'border-primary/60 bg-primary/20 text-primary' : 'border-line text-muted hover:text-foreground',
                  !group.active && 'opacity-60',
                )}
              >
                {group.label}
                <span className={cn('ml-1.5 text-xs font-normal', on ? 'text-primary/80' : 'text-muted')}>
                  {group.active ? group.values.length : 'hidden'}
                </span>
              </button>
            )
          })}
          {newGroup === null && (
            <button
              type="button"
              onClick={() => setNewGroup({ label: '', display: 'select' })}
              className="inline-flex items-center gap-1 rounded-lg border border-dashed border-foreground/30 px-3 py-1.5 text-sm font-semibold text-muted hover:border-primary/60 hover:text-primary"
            >
              <Plus className="size-3.5" /> Add group
            </button>
          )}
        </div>
        {newGroup !== null && (
          <form
            className="mt-3 grid gap-2 border-t border-line pt-3 sm:flex"
            onSubmit={(e) => {
              e.preventDefault()
              if (newGroup.label.trim()) addGroup.mutate({ label: newGroup.label.trim(), display: newGroup.display })
            }}
          >
            <Input
              autoFocus
              value={newGroup.label}
              maxLength={60}
              placeholder="e.g. Pocket type"
              onChange={(e) => setNewGroup({ ...newGroup, label: e.target.value })}
              className="sm:flex-1"
            />
            <Select
              value={newGroup.display}
              onChange={(value) => setNewGroup({ ...newGroup, display: value as Display })}
              options={displayOptions}
              className="w-full sm:w-40"
            />
            <div className="flex gap-2">
              <Button type="submit" loading={addGroup.isPending} disabled={!newGroup.label.trim()} className="flex-1 sm:flex-none">
                <Plus className="size-4" /> Add group
              </Button>
              <Button type="button" variant="ghost" onClick={() => setNewGroup(null)}>
                Cancel
              </Button>
            </div>
          </form>
        )}
      </Card>

      {!selected ? (
        <Card className="mb-5">
          <p className="text-sm text-muted">No option groups yet. Add one above, then add its values.</p>
        </Card>
      ) : (
        <>
          <Card className="mb-5">
            <SectionLabel>{selected.label}</SectionLabel>
            <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_12rem]">
              <Field label="Name">
                <div className="flex items-center gap-1.5">
                  <Input
                    value={labelValue}
                    maxLength={60}
                    onChange={(e) => setLabelDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && labelDirty) updateGroup.mutate({ id: selected.id, label: labelValue.trim() })
                      if (e.key === 'Escape') setLabelDraft(null)
                    }}
                  />
                  {labelDirty && (
                    <Button
                      variant="ghost"
                      className="px-2"
                      loading={updateGroup.isPending && updateGroup.variables?.label !== undefined}
                      onClick={() => updateGroup.mutate({ id: selected.id, label: labelValue.trim() })}
                    >
                      Save
                    </Button>
                  )}
                </div>
              </Field>
              <Field label="Shown as">
                <Select
                  value={selected.display}
                  onChange={(value) => value !== selected.display && updateGroup.mutate({ id: selected.id, display: value as Display })}
                  options={displayOptions}
                />
              </Field>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                disabled={updateGroup.isPending}
                onClick={() => updateGroup.mutate({ id: selected.id, active: !selected.active })}
              >
                {selected.active ? (
                  <>
                    <EyeOff className="size-4" /> Hide from products
                  </>
                ) : (
                  <>
                    <Eye className="size-4" /> Show on products
                  </>
                )}
              </Button>
              <Button variant="ghost" disabled={groupIndex <= 0 || reorderGroups.isPending} onClick={() => moveGroup(-1)}>
                <ArrowLeft className="size-4" /> Earlier
              </Button>
              <Button variant="ghost" disabled={groupIndex >= groups.length - 1 || reorderGroups.isPending} onClick={() => moveGroup(1)}>
                Later <ArrowRight className="size-4" />
              </Button>
              <Button variant="danger" className="sm:ml-auto" onClick={() => setDeletingGroup(selected)}>
                <Trash2 className="size-4" /> Delete group
              </Button>
            </div>
            <p className="mt-3 text-xs text-muted">
              {selected.active
                ? `Offered by ${selected.products_count} ${selected.products_count === 1 ? 'product' : 'products'}. Earlier groups show first on the product page.`
                : 'Hidden: product edit, the product page and new orders leave it out. Products keep their choices for when it is shown again, and past orders still show it.'}
            </p>
          </Card>

          <Card className="mb-5">
            <SectionLabel>Add a value to {selected.label}</SectionLabel>
            <form
              className="grid gap-2 sm:flex"
              onSubmit={(e) => {
                e.preventDefault()
                if (valueText.trim()) addValue.mutate({ id: selected.id, value: valueText.trim() })
              }}
            >
              <Input
                value={valueText}
                onChange={(e) => setValueText(e.target.value)}
                maxLength={100}
                placeholder="e.g. DRY FIT"
                className="sm:flex-1"
              />
              <Button type="submit" loading={addValue.isPending} disabled={!valueText.trim()}>
                <Plus className="size-4" /> Add value
              </Button>
            </form>
          </Card>

          <Table columns={pictures ? ['#', 'Picture', 'Value', 'Skips', 'Order', actionsColumn] : ['#', 'Value', 'Skips', 'Order', actionsColumn]}>
            {values.length === 0 && (
              <EmptyRow colSpan={pictures ? 6 : 5}>No values in {selected.label} yet - products have nothing to offer in it.</EmptyRow>
            )}
            {values.map((value, index) => {
              const draft = edits[value.id]
              const dirty = draft !== undefined && draft.trim() !== '' && draft.trim() !== value.value
              const renaming = renameValue.isPending && renameValue.variables?.id === value.id
              const pictureBusy =
                (uploadImage.isPending && uploadImage.variables?.id === value.id) ||
                (removeImage.isPending && removeImage.variables === value.id)

              return (
                <Row key={value.id}>
                  <Cell className="w-10 text-muted">{index + 1}</Cell>
                  {pictures && (
                    <Cell>
                      <PictureCell
                        value={value}
                        busy={pictureBusy}
                        onUpload={(file) => uploadImage.mutate({ id: value.id, file })}
                        onRemove={() => removeImage.mutate(value.id)}
                      />
                    </Cell>
                  )}
                  <Cell>
                    <div className="flex min-w-56 items-center gap-1.5">
                      <Input
                        value={draft ?? value.value}
                        maxLength={100}
                        onChange={(e) => setEdits({ ...edits, [value.id]: e.target.value })}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && dirty) renameValue.mutate({ id: value.id, value: draft.trim() })
                          if (e.key === 'Escape') setEdits({ ...edits, [value.id]: value.value })
                        }}
                      />
                      {dirty && (
                        <Button variant="ghost" className="px-2" loading={renaming} onClick={() => renameValue.mutate({ id: value.id, value: draft.trim() })}>
                          Save
                        </Button>
                      )}
                    </div>
                  </Cell>
                  <Cell>
                    {/* Groups this value makes unnecessary: Muslimah skips Sleeve type. */}
                    <button
                      type="button"
                      onClick={() => setSkipping({ value, keys: value.skips ?? [] })}
                      className={cn(
                        'whitespace-nowrap rounded-lg border px-2.5 py-1 text-xs font-semibold',
                        value.skips?.length ? 'border-primary/60 bg-primary/20 text-primary' : 'border-line text-muted hover:text-foreground',
                      )}
                    >
                      {skipLabel(value.skips)}
                    </button>
                  </Cell>
                  <Cell>
                    <div className="flex items-center">
                      <button
                        type="button"
                        aria-label="Move up"
                        disabled={index === 0 || reorderValues.isPending}
                        onClick={() => moveValue(index, -1)}
                        className="-m-2 flex size-10 items-center justify-center rounded-lg text-muted hover:bg-foreground/5 hover:text-foreground disabled:opacity-30"
                      >
                        <ArrowUp className="size-4" />
                      </button>
                      <button
                        type="button"
                        aria-label="Move down"
                        disabled={index === values.length - 1 || reorderValues.isPending}
                        onClick={() => moveValue(index, 1)}
                        className="-m-2 ml-2 flex size-10 items-center justify-center rounded-lg text-muted hover:bg-foreground/5 hover:text-foreground disabled:opacity-30"
                      >
                        <ArrowDown className="size-4" />
                      </button>
                    </div>
                  </Cell>
                  <Cell className={actionsCellClass}>
                    <button
                      type="button"
                      aria-label={`Delete ${value.value}`}
                      onClick={() => setDeletingValue(value)}
                      className="-m-2 flex size-10 items-center justify-center rounded-lg text-muted hover:bg-danger/10 hover:text-status-danger"
                    >
                      <Trash2 className="size-4" />
                    </button>
                  </Cell>
                </Row>
              )
            })}
          </Table>
        </>
      )}

      <Card className="mt-5 border-primary/20 bg-primary/5">
        <p className="flex items-start gap-2 text-sm text-foreground/80">
          <Info className="mt-0.5 size-4 shrink-0 text-primary" />
          <span>
            Tick the values each product offers, and set any add-on price, in product edit. Renaming a value here updates
            every product that offers it, add-on price included; deleting one takes it off those products. Past orders
            always keep the wording they were placed with. Skips names the groups a value makes unnecessary: MUSLIMAH
            skipping Sleeve type greys Sleeve type out as not needed on the order forms once Muslimah is picked, and the
            order is saved without one. A hidden group disappears from product edit, the product page
            and new orders, and comes back with the products' choices intact when shown again. Pictures show on the product
            page for groups shown as Pictures. Categories are added in product edit, and couriers under Settings &gt;
            Delivery.
          </span>
        </p>
      </Card>

      <Modal
        open={!!skipping}
        onClose={() => setSkipping(null)}
        title={skipping ? `What ${skipping.value.value} skips` : ''}
        footer={
          <>
            <Button variant="ghost" onClick={() => setSkipping(null)}>
              Cancel
            </Button>
            <Button
              loading={saveSkips.isPending}
              onClick={() => skipping && saveSkips.mutate({ id: skipping.value.id, skips: skipping.keys })}
            >
              Save
            </Button>
          </>
        }
      >
        <p className="text-sm text-muted">
          When {skipping?.value.value} is picked, the groups ticked here are greyed out as not needed on the product page
          and the manual order form, and the order is saved without them. Applies on every product that offers{' '}
          {skipping?.value.value}. Past orders keep what they were placed with.
        </p>
        <div className="mt-3 grid gap-2">
          {groups
            .filter((group) => group.id !== selected?.id)
            .map((group) => (
              <label key={group.key} className="flex items-center gap-2 text-sm font-semibold">
                <input
                  type="checkbox"
                  className="size-4 accent-brand-500"
                  checked={skipping?.keys.includes(group.key) ?? false}
                  onChange={(e) =>
                    skipping &&
                    setSkipping({
                      ...skipping,
                      keys: e.target.checked ? [...skipping.keys, group.key] : skipping.keys.filter((key) => key !== group.key),
                    })
                  }
                />
                {group.label}
                {!group.active && <span className="text-xs font-normal text-muted">(hidden)</span>}
              </label>
            ))}
        </div>
      </Modal>

      <ConfirmDialog
        open={!!deletingValue}
        onClose={() => setDeletingValue(null)}
        onConfirm={() => deletingValue && deleteValue.mutate(deletingValue.id)}
        title={deletingValue ? `Delete "${deletingValue.value}"?` : ''}
        confirmLabel="Delete"
        variant="danger"
        loading={deleteValue.isPending}
      >
        {(deletingValue?.products_count ?? 0) > 0
          ? `It comes off the ${deletingValue!.products_count} ${deletingValue!.products_count === 1 ? 'product' : 'products'} that offer it, with any add-on price. Past orders keep it.`
          : 'No product offers it. Past orders that used it keep it.'}
      </ConfirmDialog>

      <ConfirmDialog
        open={!!deletingGroup}
        onClose={() => setDeletingGroup(null)}
        onConfirm={() => deletingGroup && deleteGroup.mutate(deletingGroup.id)}
        title={deletingGroup ? `Delete the ${deletingGroup.label} group?` : ''}
        confirmLabel="Delete"
        variant="danger"
        loading={deleteGroup.isPending}
      >
        {(deletingGroup?.products_count ?? 0) > 0
          ? `${deletingGroup!.label} is offered by ${deletingGroup!.products_count} ${deletingGroup!.products_count === 1 ? 'product' : 'products'}, so it cannot be deleted. Hide it instead - past orders still show it.`
          : 'Only a group that no product offers and no order has used can be deleted. If an order used it, the server refuses and you can hide it instead.'}
      </ConfirmDialog>
    </div>
  )
}

/** Thumbnail plus upload, replace and remove for one value of a picture group. */
function PictureCell({
  value,
  busy,
  onUpload,
  onRemove,
}: {
  value: AdminOptionValue
  busy: boolean
  onUpload: (file: File) => void
  onRemove: () => void
}) {
  const input = useRef<HTMLInputElement>(null)
  // Keyed by path, so a replaced picture gets a fresh chance to load.
  const [brokenPath, setBrokenPath] = useState<string | null>(null)
  const showImage = value.image_path && brokenPath !== value.image_path

  return (
    <div className="flex items-center gap-2">
      <span className="flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-foreground/10 bg-background">
        {showImage ? (
          <img src={value.image_path!} alt="" className="size-full object-contain" onError={() => setBrokenPath(value.image_path)} />
        ) : (
          <ImageOff className="size-4 text-muted" />
        )}
      </span>
      <button
        type="button"
        disabled={busy}
        onClick={() => input.current?.click()}
        className="inline-flex items-center gap-1 whitespace-nowrap rounded-lg px-2 py-2 text-xs font-semibold text-primary hover:bg-primary/5 disabled:opacity-50"
      >
        {busy ? <Loader2 className="size-3.5 animate-spin" /> : <Upload className="size-3.5" />}
        {value.image_path ? 'Replace' : 'Upload'}
      </button>
      {value.image_path && (
        <button
          type="button"
          aria-label={`Remove the picture of ${value.value}`}
          disabled={busy}
          onClick={onRemove}
          className="-m-2 flex size-10 items-center justify-center rounded-lg text-muted hover:text-status-danger disabled:opacity-50"
        >
          <X className="size-3.5" />
        </button>
      )}
      <input
        ref={input}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) onUpload(file)
          e.target.value = ''
        }}
      />
    </div>
  )
}
